/**
 * Which of the two apps this build is.
 *
 * `public` is the app anybody reaches: masked rows, no login, the anonymous
 * order flow. `member` is the app behind the subscription: rows of counts and
 * indicators, the member home, nothing masked.
 *
 * They are separate deployments under separate subdomains rather than one
 * bundle that decides at runtime, and the reason is the state in between. A
 * single build has to answer "signed in?" on every screen, which means a
 * session lapsing mid-visit silently turns a member into a visitor: the row
 * component changes underneath the customer, the hydration endpoint changes
 * with it, and what they were reading comes back masked with nothing saying
 * why. Deploying twice deletes that state instead of handling it — in the
 * member build a lapsed session is a trip to the login screen, because the
 * shell was decided when the bundle was built and cannot change under it.
 *
 * Note this is a build-time *decision*, not tree-shaking: both row components
 * are still imported and both ship. What does not ship is the possibility of
 * changing between them while somebody is looking.
 */
export type AppMode = 'public' | 'member';

interface EnvironmentConfig {
  /** Which app this build is. See {@link AppMode}. */
  mode: AppMode;

  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // Meta Pixel Configuration
  metaPixel: {
    enabled: boolean;
    pixelId: string;
  };

  maps: {
    /**
     * The key for Google's Maps **Embed** API, used by the company profile.
     *
     * The Embed API is free with no monthly cap — a different SKU from the
     * Maps JavaScript API, which allows 10,000 loads a month and then bills.
     * That distinction is the reason the profile uses an iframe rather than a
     * map we draw: the profile is the metered route, and one organisation at
     * its fair-use ceiling would exceed the JavaScript free tier three times
     * over on its own.
     *
     * Empty simply hides the map. It is decoration on a screen that works
     * without it, so this is not worth failing a build over the way an absent
     * Turnstile key is.
     */
    embedKey: string;
  };

  // Sentry Configuration
  sentry: {
    enabled: boolean;
    dsn: string;
    environment: string;
  };

  // Feature Flags
  features: {
    mockServices: boolean;
    debugging: boolean;
    errorReporting: boolean;

    /**
     * Whether this build has customer accounts at all.
     *
     * The same question as `mode === 'member'`, kept as a flag because that is
     * what every screen already asks. Off is not a degraded state: it is the
     * app exactly as it was before accounts existed. Nothing prompts for a
     * login, /login sends you back to the search, and no screen offers to sign
     * in.
     *
     * The backend keeps its account routes whatever this says — they are
     * harmless unasked — so the member deployment is a rebuild of this app and
     * nothing else. That is the point of it being here rather than there: it
     * is the customer-facing half that has to be able to disappear.
     */
    accounts: boolean;
  };

  // Application Settings
  app: {
    name: string;
    version: string;
    defaultCountry: string;
    supportedCantons: string[];
  };
}

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// Environment variables with defaults
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue;
};

const getEnvBool = (key: string, defaultValue: boolean = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
};

/**
 * The mode this build was compiled for.
 *
 * Anything but `member` is public, including a typo. The public app is the
 * safe one to be by accident: it masks everything and offers no login, so a
 * misspelled value costs a member deployment its features rather than showing
 * a visitor unmasked data.
 */
const appMode: AppMode = getEnvVar('VITE_APP_MODE', 'public') === 'member' ? 'member' : 'public';

export const env: EnvironmentConfig = {
  mode: appMode,

  // API Configuration
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'https://api.ainova.ch'),
  apiTimeout: parseInt(getEnvVar('VITE_API_TIMEOUT', '60000'), 10),

  // Meta Pixel Configuration
  metaPixel: {
    enabled: getEnvBool('VITE_META_PIXEL_ENABLED', !isDevelopment),
    pixelId: getEnvVar('VITE_META_PIXEL_ID', '')
  },

  maps: {
    embedKey: getEnvVar('VITE_GOOGLE_MAPS_EMBED_KEY', '')
  },

  // Sentry Configuration
  sentry: {
    enabled: getEnvBool('VITE_SENTRY_ENABLED', isProduction),
    dsn: getEnvVar('VITE_SENTRY_DSN', ''),
    environment: getEnvVar('VITE_SENTRY_ENVIRONMENT', isDevelopment ? 'development' : 'production')
  },

  // Feature Flags
  features: {
    mockServices: getEnvBool('VITE_MOCK_SERVICES', isDevelopment),
    debugging: getEnvBool('VITE_DEBUG', isDevelopment),
    errorReporting: getEnvBool('VITE_ERROR_REPORTING_ENABLED', !isDevelopment),
    // Follows the mode, and cannot be set against it: an app with accounts is
    // the member app, and a member app without accounts has no way in. The old
    // VITE_ACCOUNTS_ENABLED still turns it on by itself, for the local runs
    // and the tests that predate the mode.
    accounts: appMode === 'member' || getEnvBool('VITE_ACCOUNTS_ENABLED', false)
  },

  // Application Settings
  app: {
    name: getEnvVar('VITE_APP_NAME', 'Swiss Company Data'),
    version: getEnvVar('VITE_APP_VERSION', '1.0.0'),
    defaultCountry: getEnvVar('VITE_DEFAULT_COUNTRY', 'CH'),
    supportedCantons: [
      'AG',
      'AI',
      'AR',
      'BE',
      'BL',
      'BS',
      'FR',
      'GE',
      'GL',
      'GR',
      'JU',
      'LU',
      'NE',
      'NW',
      'OW',
      'SG',
      'SH',
      'SO',
      'SZ',
      'TG',
      'TI',
      'UR',
      'VD',
      'VS',
      'ZG',
      'ZH'
    ]
  }
};

/**
 * The origin this build talks to, for a `connect-src` directive.
 *
 * # Why it is derived rather than written out
 *
 * Two pages carry a CSP `meta` of their own — the checkout and the thank-you
 * page, both reached with an order in flight. They named `https://api.ainova.ch`
 * as a literal and then added `http://localhost:8181` beside it so that
 * development worked, which put a rule about somebody's laptop into every
 * production bundle. Neither entry was ever right: the API origin is a
 * build-time decision like every other, and a build pointed at a third
 * environment would have been refused by its own policy.
 *
 * Scheme and host only. `connect-src` matches origins, and a path in the
 * directive is ignored at best.
 */
export function apiOrigin(): string {
  try {
    return new URL(env.apiBaseUrl).origin;
  } catch {
    // An unparseable value cannot be turned into a directive, and a policy
    // built around a broken one would block the calls it exists to allow.
    // `'self'` stays either way, so the page still loads.
    return '';
  }
}
